import React from "react";
import { useSelector } from 'react-redux';
import { useTheme, Box, Typography, Chip, Divider } from "@mui/material";
import { LAYOUT_HEIGHT } from 'state/constants';
import { formatTZDate } from 'state/helpers';
import FlexBetween from "components/FlexBetween";
import FlexCenter from 'components/FlexCenter';


const UnfinalizedRouteList = ({ routes, onView }) => {
    const timeMode = useSelector((state) => state.timemode);
    const params = useSelector((state) => state.params);
    const crewRanksData = useSelector((state) => state.crewRanks);
    const theme = useTheme();
    const minCrewRouteTAT = Number(params?.find((item) => item.param === "MINIMUM_CREW_ROUTE_TAT_MINUTES")?.value) || 80;

    if (!Array.isArray(routes) || routes.length === 0) {
        return (
            <FlexCenter height={LAYOUT_HEIGHT}>
                <Typography color="error">Nothing to display</Typography>
            </FlexCenter>
        );
    }
    
    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {routes.map((route, idx) => {
                const routeDate = timeMode === "UTC"
                    ? formatTZDate(route?.routeDate)
                    : formatTZDate(route?.routeDate);

                const sortedCrewMembers = [...route.crewMembers]
                    ?.map(member => {
                        const rank = crewRanksData.find(rank => rank?.rankCode === member?.post) || {};
                        return { ...member, rankPriority: rank?.rankPriority || Infinity, rankType: rank?.rankType };
                    })
                    ?.sort((a, b) => a?.rankPriority - b?.rankPriority);

                const hasWarning = route.routeDetails?.some((flight) => 
                    flight.diffRegScheduleTat !== null && 
                    flight.diffRegScheduleTat < minCrewRouteTAT
                );
                
                return (
                    <Box
                        key={route._id || idx}
                        sx={{
                            border: `1px solid ${theme.palette.neutral.deep}`,
                            borderRadius: 2,
                            p: 1.5,
                            backgroundColor: "background.paper",
                            transition: "all 0.25s ease",
                            "&:hover": {
                                boxShadow: 2,
                                bgcolor: theme.palette.action.hover,
                            },
                            "&:active": {
                                transform: "scale(0.98)",
                                boxShadow: 1,
                            },
                        }}
                        onClick={() => { onView(route) }}
                    >
                        <Box
                            sx={{
                                animation: hasWarning
                                    ? "breathingGlow 3s ease-in-out infinite"
                                    : "none",
                                "@keyframes breathingGlow": {
                                    "0%, 100%": {
                                        transform: "scale(1)",
                                        boxShadow: "0 0 0px rgba(255, 215, 0, 0.25)", 
                                    },
                                    "50%": {
                                        transform: "scale(1.02)",
                                        boxShadow: "0 0 4px 2px rgba(255, 235, 100, 0.6)", 
                                    },
                                },
                            }}
                        >
                            <FlexBetween>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Chip
                                        label={idx + 1}
                                        variant="outlined"
                                        color={hasWarning ? "warning" : "primary"}
                                        size="small"
                                        sx={{
                                            fontSize: "0.75rem",
                                            borderWidth: 1.5,
                                            height: 20,
                                            "& .MuiChip-label": {
                                                px: 0.8,
                                            },
                                        }}
                                    />

                                    <Typography variant="subtitle2" sx={{ lineHeight: 1 }}>
                                        {routeDate}
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ lineHeight: 1 }}>
                                        {route.routeNo}
                                    </Typography>
                                    <Typography
                                        variant="subtitle2"
                                        title={route.crewRoute}
                                        sx={{
                                            lineHeight: 1,
                                            maxWidth: 200,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {(route.crewRoute?.length > 23
                                            ? route.crewRoute.slice(0, 23) + "..."
                                            : route.crewRoute) || ""}
                                    </Typography>
                                </Box>
                                {/* <Typography variant="caption" color="text.secondary">
                                    {route.acType || ""} | {route.flightReg?.split("-")[1] || ""}
                                </Typography> */}
                            </FlexBetween>                            
                        </Box>
                        
                        <Divider sx={{ my: 0.5 }} />
                        
                        {sortedCrewMembers?.map((member, index) => { 
                            return (
                                <Box
                                    key={index}
                                    display="flex"
                                    alignItems="center"
                                    alignContent='flex-start'
                                    gap={1}
                                    sx={{
                                    color: member?.gender === 'FM' 
                                        ? theme.palette.secondary.main 
                                        : theme.palette.primary.dark,
                                    }}
                                >
                                    <Typography 
                                        width="18px" 
                                        textAlign='right' 
                                    >
                                        {index + 1}. 
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'right',
                                            whiteSpace: 'nowrap',
                                            width: '30px',
                                        }}
                                    >
                                        {member.crewCode}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'left',
                                        }}
                                    >
                                        {member.crewName}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'center',
                                        }}
                                    >
                                        ({member.post} | {member.base}{member.trnDuty && ` | ${member.trnDuty}`})
                                    </Typography>                
                                </Box>
                            )
                        })}
                    </Box>
                )
            })}
        </Box>
    );
};

export default UnfinalizedRouteList;
